import { Button } from 'primereact/button';
import { Chart } from 'primereact/chart';
import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FaFileExcel } from 'react-icons/fa';
import AppCard from '../../../components/appCard';
import { useInfo } from '../../../context/infoContext';
import { getSMSDataGrafico, getTransationInfo } from '../../../services/infoServices';
import { DropdownPague } from '../../../styles/primeReactConfig';
import { formatCurrency } from '../../../utils/formatCurrency';
import GeraXLSX from '../../../utils/GeraXLSX';

export default function Estatisticas() {

  const dt = useRef()
  const firstRender = useRef(true);

  const {
    graficosInfo,
    dashboardInfo,
    providerInfo
  } = useInfo();

  const currentYear = new Date().getFullYear()

  const yearOptions = Array.from({ length: 5 }, (_, i) => {
    const year = currentYear - i
    return { label: year.toString(), value: year }
  })

  const [smsYear, setSmsYear] = useState(currentYear)
  const [paymentYear, setPaymentYear] = useState(currentYear)

  const [smsLoading, setSmsLoading] = useState(false)
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [loadingCSV, setLoadingCSV] = useState(false);

  const [smsData, setSMSData] = useState({
    labels: graficosInfo?.comunicacoes?.mes || [],
    datasets: [
      {
        label: 'SMSs Enviados',
        backgroundColor: '#4aa677',
        data: graficosInfo?.comunicacoes?.sms || [],
        borderColor: 'white',
      },
      {
        label: 'E-mails Enviados',
        backgroundColor: document.documentElement.className === 'dark' ? "#2e5168" : '#012440',
        data: graficosInfo?.comunicacoes?.email || [],
        borderColor: 'white',
      }
    ],
  })

  const [paymentHist, setPaymentHist] = useState({
    labels: graficosInfo?.transacoes?.meses || [],
    datasets: [
      {
        label: 'Valor dos Repasses',
        backgroundColor: "#4aa677",
        data: graficosInfo?.transacoes?.data || [],
        borderColor: "white",
      }
    ]
  })

  const totalPlugin = {
    id: 'totalPlugin',
    afterDraw: (chart) => {
      const { ctx, chartArea } = chart
      const { top, right } = chartArea

      const dataset = chart.data.datasets[0].data
      const total = dataset.reduce((a, b) => a + Number(b), 0)

      ctx.save()
      ctx.font = 'bold 14px sans-serif'
      ctx.textAlign = 'right'
      ctx.textBaseline = 'top'
      ctx.fillStyle = document.documentElement.className === 'dark' ? 'white' : 'black'

      ctx.fillText(`Total: ${formatCurrency(total)}`, right - 10, top + 5)

      ctx.restore()
    }
  }
  const totalPluginSMS = {
    id: 'totalPlugin',
    afterDraw: (chart) => {
      const { ctx, chartArea } = chart
      const { top, right } = chartArea

      const dataset_sms = chart.data.datasets[0].data
      const total_sms = dataset_sms.reduce((a, b) => a + (b || 0), 0)
      const dataset_email = chart.data.datasets[1].data
      const total_email = dataset_email.reduce((a, b) => a + (b || 0), 0)

      ctx.save()
      ctx.font = 'bold 14px sans-serif'
      // efeito tipo text-shadow
      ctx.shadowColor = document.documentElement.className === 'dark' ? 'black' : 'white';
      ctx.shadowBlur = 4
      ctx.shadowOffsetX = 1
      ctx.shadowOffsetY = 1
      ctx.textAlign = 'right'
      ctx.textBaseline = 'top'
      ctx.fillStyle = document.documentElement.className === 'dark' ? 'white' : 'black'

      ctx.fillText(`Total SMS: ${total_sms.toLocaleString('pt-BR')}`, right - 10, top + 5)
      ctx.fillText(`Total Email: ${total_email.toLocaleString('pt-BR')}`, right - 10, top + 25)

      ctx.restore()
    }
  }


  /*
  =========================
  CARREGA SMS DO BACKEND
  =========================
  */

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    let cancelled = false

    const loadSMS = async () => {
      setSmsLoading(true)
      const sms_data = await getSMSDataGrafico(
        providerInfo.personal?.usuario_documento,
        12,
        smsYear
      )

      if (cancelled) return

      setSMSData({
        labels: sms_data.mes,
        datasets: [
          {
            label: 'SMSs Enviados',
            backgroundColor: '#4aa677',
            data: sms_data.sms,
            borderColor: 'white',
          },
          {
            label: 'E-mails Enviados',
            backgroundColor: document.documentElement.className === 'dark'
              ? "#2e5168"
              : '#012440',
            data: sms_data.email,
            borderColor: 'white',
          }
        ],
      })
      setSmsLoading(false)
    }

    if (providerInfo.personal?.usuario_documento) {
      loadSMS()
    }
    return () => {
      cancelled = true
    }

  }, [smsYear, providerInfo.personal?.usuario_documento])


  /*
  =========================
  CARREGA REPASSES BACKEND
  =========================
  */

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    let cancelled = false

    const loadPayments = async () => {
      setPaymentLoading(true)
      const transation_data = await getTransationInfo(paymentYear)
      if (cancelled) return
      setPaymentHist({
        labels: transation_data.meses,
        datasets: [
          {
            label: 'Valor dos Repasses',
            backgroundColor: "#4aa677",
            data: transation_data.data,
            borderColor: "white",
          }
        ]
      })
      setPaymentLoading(false)
    }

    loadPayments()
    return () => {
      cancelled = true
    }

  }, [paymentYear])


  const handleExportSMS = () => {
    const rows = []
    smsData.labels.forEach((mes, i) => {
      rows.push({
        mes_ano: mes,
        sms: !smsData.datasets[0]?.data[i] ? 0 : smsData.datasets[0]?.data[i],
        email: !smsData.datasets[1]?.data[i] ? 0 : smsData.datasets[1]?.data[i]
      })
    })

    GeraXLSX({
      data: rows,
      headers: ['Mês', 'SMS Enviados', 'Emails Enviados'],
      fields: ["mes_ano", "sms", "email"],
      titulo: `Comunicacoes_${smsYear}`
    })
  }

  const handleExportPagamentos = () => {
    const rows = []
    paymentHist.labels.forEach((mes, i) => {
      rows.push({
        mes_ano: mes,
        valor: paymentHist.datasets[0].data[i]
      })
    })

    GeraXLSX({
      data: rows,
      headers: ['Mês', 'Valor'],
      fields: ["mes_ano", "valor"],
      titulo: `Repasses_${paymentYear}`
    })
  }


  const legendColor = document.documentElement.className === 'dark' ? 'white' : 'black';

  const options = useMemo(() => ({
    maintainAspectRatio: false,
    aspectRatio: 1,
    plugins: {
      legend: {
        labels: {
          color: legendColor
        },
      },
    },
    scales: {
      x: {
        ticks: { color: legendColor }
      },
      y: {
        ticks: { color: legendColor }
      },
    }
  }), [dashboardInfo]);

  const optionsRound = useMemo(() => ({
    plugins: {
      legend: {
        labels: {
          color: legendColor
        },
      },
    },
  }), [dashboardInfo]);

  const pieData = useMemo(() => ({
    labels: ['Pagos', 'Em Negociação', 'Em Aberto', 'Protestados'],
    datasets: [
      {
        data: [
          dashboardInfo.valores.total_cobrancas_pagas,
          dashboardInfo.valores.total_cobrancas_pendentes,
          dashboardInfo.valores.total_cobrancas_s_negociacao,
          dashboardInfo.valores.total_protestados,
        ],
        backgroundColor: [
          "#4aa677",
          "#4aa6a5",
          document.documentElement.className === 'dark' ? "#2e5168" : '#012440',
          "#e7e378"
        ]
      }
    ]
  }), [dashboardInfo]);

  const doughnutData = useMemo(() => ({
    labels: ['Pagos', 'Em Negociação', 'Em Aberto', 'Protestados'],
    datasets: [
      {
        data: [
          dashboardInfo.valores.qtd_total_cobrancas_pagas,
          dashboardInfo.valores.qtd_total_cobrancas_pendentes,
          dashboardInfo.valores.qtd_total_cobrancas_s_negociacao,
          dashboardInfo.valores.qtd_total_protestados,
        ],
        backgroundColor: [
          "#4aa677",
          "#4aa6a5",
          document.documentElement.className === 'dark' ? "#2e5168" : '#012440',
          "#e7e378"
        ]
      }
    ]
  }), [dashboardInfo]);


  return (
    <div className="flex flex-wrap -mt-2">

      <div className="w-full lg:w-1/2 p-2">
        <AppCard className="items-center">
          <div className='w-5/6'>
            <h5 className='text-xl mb-4'>Valor das Negociações</h5>
            <Chart type="pie" options={optionsRound} data={pieData} className='w-full' />
          </div>
        </AppCard>
      </div>

      <div className="w-full lg:w-1/2 p-2">
        <AppCard className="items-center">
          <div className='w-5/6'>
            <h5 className='text-xl mb-4'>Quantidade de Negociações</h5>
            <Chart type="pie" options={optionsRound} data={doughnutData} className='w-full' />
          </div>
        </AppCard>
      </div>


      {/* SMS */}

      <div className="w-full lg:w-1/2 p-2">
        <AppCard className="items-center">

          <div className='flex justify-between items-center w-full mb-3'>
            <h5 className='text-xl'>Histórico de Comunicações</h5>

            <div className='flex gap-2 items-center'>
              <Button
                type="button"
                icon={<FaFileExcel className="text-2xl" />}
                onClick={() => handleExportSMS()}
                pt={{
                  root: `hover:dark:bg-green-500 hover:bg-green-600 
                  dark:bg-green-400 bg-green-500 p-3 rounded-md
                  text-white dark:text-black transition duration-300
                  w-[3rem] h-[3rem]`,
                  label: "absolute",
                }}
                tooltip="Exportar em XLSX"
                loading={smsLoading}
              />
              <Dropdown
                pt={DropdownPague}
                value={smsYear}
                options={yearOptions}
                onChange={(e) => setSmsYear(e.value)}
                className="w-28"
              />
            </div>
          </div>

          {smsLoading
            ? <ProgressSpinner strokeWidth="6" />
            : <Chart type="bar" options={options} ref={dt} data={smsData} style={{ width: '100%' }} plugins={[totalPluginSMS]} />}
        </AppCard>
      </div>


      {/* REPASSES */}

      <div className="w-full lg:w-1/2 p-2">
        <AppCard className="items-center">

          <div className='flex justify-between items-center w-full mb-3'>
            <h5 className='text-xl'>Histórico de Pagamentos</h5>
            <div className='flex gap-2 items-center'>
              <Button
                type="button"
                icon={<FaFileExcel className="text-2xl" />}
                onClick={() => handleExportPagamentos()}
                pt={{
                  root: `hover:dark:bg-green-500 hover:bg-green-600 
                  dark:bg-green-400 bg-green-500 p-3 rounded-md
                  text-white dark:text-black transition duration-300
                  w-[3rem] h-[3rem]`,
                  label: "absolute",
                }}
                tooltip="Exportar em XLSX"
                loading={paymentLoading}
              />
              <Dropdown
                pt={DropdownPague}
                value={paymentYear}
                options={yearOptions}
                onChange={(e) => setPaymentYear(e.value)}
                className="w-28"
              />
            </div>
          </div>

          {paymentLoading
            ? <ProgressSpinner strokeWidth="6" />
            : <Chart type="bar" options={options} data={paymentHist} style={{ width: '100%' }} plugins={[totalPlugin]} />}
        </AppCard>
      </div>

    </div>
  )
}